import { generateGroupId, generateQuestionId } from '../../utils/faqLinkUtils';

export interface FaqHashTarget {
  group?: string;
  question?: string;
}

export function parseFaqHash(hash = window.location.hash): FaqHashTarget {
  const normalized = hash.replace(/^#/, '');
  if (!normalized) {
    return {};
  }

  const params = new URLSearchParams(normalized.includes('=') ? normalized : undefined);
  if (params && (params.get('faq') || params.get('group'))) {
    const group = params.get('faq') || params.get('group') || undefined;
    const question = params.get('question') || undefined;
    return { group: group || undefined, question: question || undefined };
  }

  const parts = decodeURIComponent(normalized).split(':');
  return { group: parts[0] || undefined, question: parts[1] || undefined };
}

export function buildFaqHash(group: string, question?: string): string {
  return question
    ? `faq=${encodeURIComponent(group)}&question=${encodeURIComponent(question)}`
    : `faq=${encodeURIComponent(group)}`;
}

export function updateFaqHash(group: string, question?: string): void {
  window.location.hash = buildFaqHash(group, question);
}

export async function waitFor(
  predicate: () => boolean,
  timeoutMs = 1500,
  intervalMs = 30,
): Promise<boolean> {
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      if (predicate()) {
        resolve(true);
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(false);
        return;
      }
      setTimeout(tick, intervalMs);
    };
    tick();
  });
}

export function scrollToElement(
  el: HTMLElement,
  align: 'start' | 'center' = 'start',
  duration = 900,
  offset = 0,
): Promise<void> {
  try {
    const rect = el.getBoundingClientRect();
    const startY = window.scrollY || window.pageYOffset || 0;
    let targetY = startY + rect.top;
    if (align === 'center') {
      targetY =
        startY +
        rect.top -
        (window.innerHeight / 2 - Math.min(rect.height, window.innerHeight) / 2);
    }
    if (offset) {
      targetY -= offset;
    }
    const delta = targetY - startY;
    if (Math.abs(delta) < 2) {
      return Promise.resolve();
    }
    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    return new Promise<void>((resolve) => {
      const start = performance.now();
      const step = (now: number) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = easeInOut(t);
        window.scrollTo(0, startY + delta * eased);
        if (t < 1) {
          requestAnimationFrame(step);
        } else {
          resolve();
        }
      };
      requestAnimationFrame(step);
    });
  } catch {
    el.scrollIntoView({ behavior: 'smooth', block: align });
    return Promise.resolve();
  }
}

export async function navigateFromFaqHash(
  expandGroup: (group: string) => void,
  opts?: { smooth?: boolean },
): Promise<void> {
  const { group, question } = parseFaqHash();
  if (!group) {
    return;
  }

  expandGroup(group);

  const groupId = generateGroupId(group);
  const groupSelector = () => !!document.getElementById(groupId);
  const groupExpandedSelector = () => {
    const element = document.getElementById(groupId);
    return !!element && element.classList.contains('Mui-expanded');
  };

  await waitFor(groupSelector, 10000);
  const initialGroupElement = document.getElementById(groupId);
  if (initialGroupElement) {
    const headerOffset = 80;
    if (opts?.smooth === false) {
      window.scrollTo({
        top: (window.scrollY || 0) + initialGroupElement.getBoundingClientRect().top - headerOffset,
        behavior: 'auto',
      });
    } else {
      await scrollToElement(initialGroupElement, 'start', 950, headerOffset);
    }
  }
  await waitFor(groupExpandedSelector, 10000);

  if (!question) {
    return;
  }

  const itemId = generateQuestionId(group, question);
  const itemQuery = () => document.getElementById(itemId);
  await waitFor(() => !!itemQuery(), 10000);

  const itemEl = itemQuery();
  if (!itemEl) {
    return;
  }

  const accordion = itemEl.querySelector('.MuiAccordion-root') as HTMLElement | null;
  const alreadyExpanded = !!accordion && accordion.classList.contains('Mui-expanded');
  if (!alreadyExpanded) {
    const summary = itemEl.querySelector(
      '.MuiAccordionSummary-root, [role=button]',
    ) as HTMLElement | null;
    summary?.click();
  }

  const nestedExpanded = () => {
    const nestedAccordion = itemEl.querySelector('.MuiAccordion-root');
    return !!nestedAccordion && nestedAccordion.classList.contains('Mui-expanded');
  };
  await waitFor(nestedExpanded);

  if (opts?.smooth === false) {
    itemEl.scrollIntoView({ behavior: 'auto', block: 'center' });
  } else {
    await scrollToElement(itemEl, 'center', 1000);
  }
}
